import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiMail, FiPhone, FiClock, FiSend } from 'react-icons/fi';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Form state interface
interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    // Simuleer een API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormState({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      // Reset success message na 5 seconden
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/contact-hero.jpg" 
            alt="Contact Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Neem <span className="text-lions-yellow">Contact</span> Met Ons Op
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Heeft u vragen, suggesties of wilt u meer informatie over Lions Club Hoorn De Doelen? Neem dan contact met ons op.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Informatie */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.h2 
                variants={fadeIn}
                className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow"
              >
                Contactgegevens
              </motion.h2>
              <motion.div 
                variants={fadeIn}
                className="space-y-6"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-lions-blue dark:bg-lions-yellow rounded-full p-3 text-white dark:text-lions-dark">
                    <FiMapPin size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Adres</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      Restaurant De Nadorst<br />
                      Westerblokker 171<br />
                      1695AE Blokker
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-lions-blue dark:bg-lions-yellow rounded-full p-3 text-white dark:text-lions-dark">
                    <FiMail size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">E-mail</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      <a href="mailto:hoorn.de.doelen@lions.nl" className="hover:text-lions-blue dark:hover:text-lions-yellow">
                        hoorn.de.doelen@lions.nl
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-lions-blue dark:bg-lions-yellow rounded-full p-3 text-white dark:text-lions-dark">
                    <FiPhone size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Telefoon</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      <a href="tel:0229232679" className="hover:text-lions-blue dark:hover:text-lions-yellow">
                        0229 232679
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-lions-blue dark:bg-lions-yellow rounded-full p-3 text-white dark:text-lions-dark">
                    <FiClock size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Bijeenkomsten</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      2e maandag van de maand: 18:00 uur<br />
                      4e maandag van de maand: 18:00 uur
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={fadeIn}
                className="mt-12"
              >
                <h3 className="text-xl font-bold mb-4">Locatie</h3>
                <div className="rounded-lg overflow-hidden shadow-lg h-64">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2421.0982618030934!2d5.1022!3d52.6567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c8a8e8e8e8e8e8%3A0x8e8e8e8e8e8e8e8e!2sRestaurant%20De%20Nadorst!5e0!3m2!1snl!2snl!4v1625000000000!5m2!1snl!2snl" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen 
                    loading="lazy"
                    title="Locatie Lions Club Hoorn De Doelen"
                  ></iframe>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Formulier */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.h2 
                variants={fadeIn}
                className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow"
              >
                Stuur ons een bericht
              </motion.h2>
              <motion.form 
                variants={fadeIn}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-gray-700 dark:text-gray-300 mb-2">Naam *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-lions-blue dark:focus:ring-lions-yellow"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 mb-2">E-mail *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-lions-blue dark:focus:ring-lions-yellow"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-gray-700 dark:text-gray-300 mb-2">Onderwerp *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-lions-blue dark:focus:ring-lions-yellow"
                  >
                    <option value="">Selecteer een onderwerp</option>
                    <option value="Algemene vraag">Algemene vraag</option>
                    <option value="Lid worden">Lid worden</option>
                    <option value="Sponsor worden">Sponsor worden</option>
                    <option value="Evenementen">Evenementen</option>
                    <option value="Donatie">Donatie</option>
                    <option value="Anders">Anders</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-gray-700 dark:text-gray-300 mb-2">Bericht *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-lions-blue dark:focus:ring-lions-yellow"
                  ></textarea>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary flex items-center justify-center w-full"
                  >
                    {isSubmitting ? (
                      <span>Verzenden...</span>
                    ) : (
                      <>
                        <FiSend className="mr-2" /> Verstuur bericht
                      </>
                    )}
                  </button>
                </div>
                {submitSuccess && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    <p>Bedankt voor uw bericht! We nemen zo snel mogelijk contact met u op.</p>
                  </div>
                )}
                {submitError && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <p>{submitError}</p>
                  </div>
                )}
              </motion.form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-lions-blue text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Word lid van Lions Club Hoorn De Doelen
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Wilt u ook een verschil maken in de gemeenschap? Word lid van Lions Club Hoorn De Doelen en help mee met onze projecten en activiteiten.
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="flex flex-wrap justify-center gap-4"
            >
              <button className="btn-secondary">
                Word lid
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 